import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Save, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { TERMS_CONTENT_DEFAULT, PRIVACY_CONTENT_DEFAULT, ACCESSIBILITY_CONTENT_DEFAULT } from '@/models/defaultSiteContent';

interface SiteSetting {
  key: string;
  value: string;
}

const SiteSettingsModule = () => {
  const [settings, setSettings] = useState<SiteSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value')
        .order('key');

      if (error) throw error;

      // Initialize default settings if specific keys are missing
      const settingsData = data || [];
      const requiredKeys = [
        'maintenance_mode',
        'terms_content', 'terms_last_updated',
        'privacy_content', 'privacy_last_updated',
        'accessibility_content', 'accessibility_last_updated'
      ];

      const newSettings = [...settingsData];
      let needsUpdate = false;

      requiredKeys.forEach(key => {
        if (!newSettings.find(s => s.key === key)) {
          newSettings.push({ key, value: '' });
          needsUpdate = true;
        }
      });

      setSettings(newSettings);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('שגיאה בטעינת הגדרות האתר');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key: string, value: string) => {
    setSettings(prev => {
      const existing = prev.find(s => s.key === key);
      if (existing) {
        return prev.map(s => s.key === key ? { ...s, value } : s);
      } else {
        return [...prev, { key, value }];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Sanitize settings to only include key and value
      const sanitizedSettings = settings.map(({ key, value }) => ({ key, value }));

      const { error } = await supabase
        .from('site_settings')
        .upsert(sanitizedSettings, { onConflict: 'key' });

      if (error) throw error;

      toast.success('הגדרות האתר נשמרו בהצלחה');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('שגיאה בשמירת הגדרות האתר');
    } finally {
      setSaving(false);
    }
  };

  const getSettingValue = (key: string) => {
    return settings.find(s => s.key === key)?.value || '';
  };

  const loadTermsTemplate = () => {
    updateSetting('terms_content', TERMS_CONTENT_DEFAULT);
    toast.info('טקסט ברירת מחדל נטען לתנאי שימוש');
  };

  const loadPrivacyTemplate = () => {
    updateSetting('privacy_content', PRIVACY_CONTENT_DEFAULT);
    toast.info('טקסט ברירת מחדל נטען למדיניות פרטיות');
  };

  const loadAccessibilityTemplate = () => {
    updateSetting('accessibility_content', ACCESSIBILITY_CONTENT_DEFAULT);
    toast.info('טקסט ברירת מחדל נטען להצהרת נגישות');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            הגדרות אתר ותוכן
          </CardTitle>
        </CardHeader>
        <CardContent dir="rtl">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-8">
              <TabsTrigger value="general">כללי / תחזוקה</TabsTrigger>
              <TabsTrigger value="terms">תנאי שימוש</TabsTrigger>
              <TabsTrigger value="privacy">מדיניות פרטיות</TabsTrigger>
              <TabsTrigger value="accessibility">הצהרת נגישות</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-6">
              <div className="border-2 border-warning/20 bg-warning/5 p-4 rounded-lg space-y-4">
                <div className="flex items-center gap-2 text-warning">
                  <AlertTriangle className="h-5 w-5" />
                  <Label htmlFor="maintenance_mode" className="font-bold text-lg">מצב תחזוקה</Label>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    כאשר מופעל, האתר יהיה חסום לגולשים רגילים (מנהלים מחוברים יוכלו להמשיך לגלוש).
                  </span>
                  <Switch
                    id="maintenance_mode"
                    checked={getSettingValue('maintenance_mode') === 'true'}
                    onCheckedChange={(checked) => updateSetting('maintenance_mode', checked ? 'true' : 'false')}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="terms" className="space-y-4">
              <div className="flex justify-between items-center mb-2">
                <Label htmlFor="terms_content">תוכן תנאי השימוש</Label>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-muted-foreground">השתמש ברווח כפול (אנטר פעמיים) ליצירת כרטיסייה נפרדת</span>
                  <Button variant="outline" size="sm" onClick={loadTermsTemplate}>
                    טען טקסט משפטי (ברירת מחדל)
                  </Button>
                </div>
              </div>
              <Textarea
                id="terms_content"
                value={getSettingValue('terms_content')}
                onChange={(e) => updateSetting('terms_content', e.target.value)}
                className="min-h-[400px] text-right font-sans"
                dir="rtl"
                placeholder="הזן כאן את תנאי השימוש..."
              />

              <div className="space-y-2 mt-4">
                <Label htmlFor="terms_last_updated">טקסט עדכון אחרון</Label>
                <input
                  id="terms_last_updated"
                  type="text"
                  value={getSettingValue('terms_last_updated')}
                  onChange={(e) => updateSetting('terms_last_updated', e.target.value)}
                  className="w-full p-2 border rounded-md text-right"
                  dir="rtl"
                  placeholder="לדוגמה: ינואר 2024"
                />
              </div>
            </TabsContent>

            <TabsContent value="privacy" className="space-y-4">
              <div className="flex justify-between items-center mb-2">
                <Label htmlFor="privacy_content">תוכן מדיניות הפרטיות</Label>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-muted-foreground">השתמש ברווח כפול (אנטר פעמיים) ליצירת כרטיסייה נפרדת</span>
                  <Button variant="outline" size="sm" onClick={loadPrivacyTemplate}>
                    טען טקסט משפטי (ברירת מחדל)
                  </Button>
                </div>
              </div>
              <Textarea
                id="privacy_content"
                value={getSettingValue('privacy_content')}
                onChange={(e) => updateSetting('privacy_content', e.target.value)}
                className="min-h-[400px] text-right font-sans"
                dir="rtl"
                placeholder="הזן כאן את מדיניות הפרטיות..."
              />

              <div className="space-y-2 mt-4">
                <Label htmlFor="privacy_last_updated">טקסט עדכון אחרון</Label>
                <input
                  id="privacy_last_updated"
                  type="text"
                  value={getSettingValue('privacy_last_updated')}
                  onChange={(e) => updateSetting('privacy_last_updated', e.target.value)}
                  className="w-full p-2 border rounded-md text-right"
                  dir="rtl"
                  placeholder="לדוגמה: ינואר 2024"
                />
              </div>
            </TabsContent>

            <TabsContent value="accessibility" className="space-y-4">
              <div className="flex justify-between items-center mb-2">
                <Label htmlFor="accessibility_content">תוכן הצהרת נגישות</Label>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-muted-foreground">השתמש ברווח כפול (אנטר פעמיים) ליצירת כרטיסייה נפרדת</span>
                  <Button variant="outline" size="sm" onClick={loadAccessibilityTemplate}>
                    טען טקסט לדוגמה
                  </Button>
                </div>
              </div>
              <Textarea
                id="accessibility_content"
                value={getSettingValue('accessibility_content')}
                onChange={(e) => updateSetting('accessibility_content', e.target.value)}
                className="min-h-[400px] text-right font-sans"
                dir="rtl"
                placeholder="הזן כאן את הצהרת הנגישות..."
              />

              <div className="space-y-2 mt-4">
                <Label htmlFor="accessibility_last_updated">טקסט עדכון אחרון</Label>
                <input
                  id="accessibility_last_updated"
                  type="text"
                  value={getSettingValue('accessibility_last_updated')}
                  onChange={(e) => updateSetting('accessibility_last_updated', e.target.value)}
                  className="w-full p-2 border rounded-md text-right"
                  dir="rtl"
                  placeholder="לדוגמה: ינואר 2024"
                />
              </div>
            </TabsContent>

          </Tabs>

          <div className="flex justify-end mt-8 pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 min-w-[150px]"
            >
              <Save className="h-4 w-4" />
              {saving ? 'שומר...' : 'שמור שינויים'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteSettingsModule;